import { Col, Empty, Modal, Pagination, Row, Spin, Table, Tooltip } from "antd";

import { InfoCircleOutlined } from "@ant-design/icons";
import { useWeb3ModalState } from "@web3modal/wagmi/react";
import { useEffect, useState } from "react";
import { Address } from "viem";
import { useAccount, useReadContract } from "wagmi";
import Web3 from "web3";
import { AbiItem } from "web3-utils";
import NftNodeStakingAbi from "../../config/abi/NftNodeStakingAbi.json";
import { BaseApi } from "../../config/api/BaseApi";
import { NodeTier } from "../../constants";
import { convertDateTime } from "../../utils/formatDate";
import { notiError, notiSuccess } from "../../utils/notication";

declare const window: Window &
  typeof globalThis & {
    trustwallet: any;
    ethereum: any;
    bitkeep: any;
    okexchain: any;
    safepalProvider: any;
  };

// Component for table row cells that use wagmi hook
const StakeTimeCell = ({
  item,
  stakingAddress,
  userAddress,
}: {
  item: any;
  stakingAddress: Address;
  userAddress: Address;
}) => {
  const {
    data: rewardsData,
    isLoading,
    isError,
  } = useReadContract({
    address: stakingAddress,
    abi: [
      {
        inputs: [
          {
            internalType: "address",
            name: "_user",
            type: "address",
          },
          {
            internalType: "uint256[]",
            name: "_licenseIds",
            type: "uint256[]",
          },
        ],
        name: "getUserLicenseRewards",
        outputs: [
          {
            components: [
              {
                internalType: "uint256",
                name: "stakeTime",
                type: "uint256",
              },
              {
                internalType: "uint256",
                name: "totalLockedTime",
                type: "uint256",
              },
              {
                internalType: "uint256",
                name: "totalRewards",
                type: "uint256",
              },
            ],
            internalType: "struct GuardianNodeStakingPool.LicenseLockInfo[]",
            name: "",
            type: "tuple[]",
          },
        ],
        stateMutability: "view",
        type: "function",
      },
    ],
    functionName: "getUserLicenseRewards",
    args: [userAddress, [item.id]],
    query: {
      enabled: !!userAddress && !!stakingAddress,
    },
  });

  if (isLoading) return <Spin size="small" />;
  if (isError)
    return <div className="text-white text-center text-uppercase">TBA</div>;

  const stakeTime = rewardsData?.[0]?.stakeTime
    ? String(Number(rewardsData[0].stakeTime))
    : "";

  return (
    <div className="text-white text-center text-uppercase">
      {stakeTime ? convertDateTime(new Date(Number(stakeTime) * 1000)) : "TBA"}
    </div>
  );
};

const TotalLockedTimeCell = ({
  item,
  stakingAddress,
  userAddress,
}: {
  item: any;
  stakingAddress: Address;
  userAddress: Address;
}) => {
  const {
    data: rewardsData,
    isLoading,
    isError,
  } = useReadContract({
    address: stakingAddress,
    abi: [
      {
        inputs: [
          {
            internalType: "address",
            name: "_user",
            type: "address",
          },
          {
            internalType: "uint256[]",
            name: "_licenseIds",
            type: "uint256[]",
          },
        ],
        name: "getUserLicenseRewards",
        outputs: [
          {
            components: [
              {
                internalType: "uint256",
                name: "stakeTime",
                type: "uint256",
              },
              {
                internalType: "uint256",
                name: "totalLockedTime",
                type: "uint256",
              },
              {
                internalType: "uint256",
                name: "totalRewards",
                type: "uint256",
              },
            ],
            internalType: "struct GuardianNodeStakingPool.LicenseLockInfo[]",
            name: "",
            type: "tuple[]",
          },
        ],
        stateMutability: "view",
        type: "function",
      },
    ],
    functionName: "getUserLicenseRewards",
    args: [userAddress, [item.id]],
    query: {
      enabled: !!userAddress && !!stakingAddress,
    },
  });

  if (isLoading) return <Spin size="small" />;
  if (isError) return <div className="text-white text-center">-</div>;

  const totalLockedTime = rewardsData?.[0]?.totalLockedTime
    ? Number(rewardsData[0].totalLockedTime)
    : 0;

  let time: any = "";
  let timeUnit: any = "";
  if (totalLockedTime < 3600) {
    time = totalLockedTime / 60;
    timeUnit = "Min";
  }
  if (totalLockedTime > 3600) {
    time = totalLockedTime / 3600;
    timeUnit = "Hour";
  }
  if (totalLockedTime > 86400) {
    time = totalLockedTime / 86400;
    timeUnit = "day";
    if (time > 1) {
      timeUnit = "Days";
    }
  }

  return (
    <div className="text-white text-center">
      ~
      {new Intl.NumberFormat("ja-JP", {
        maximumFractionDigits: 1,
      }).format(time)}{" "}
      {timeUnit}
    </div>
  );
};

const TotalRewardCell = ({
  item,
  stakingAddress,
  userAddress,
}: {
  item: any;
  stakingAddress: Address;
  userAddress: Address;
}) => {
  const {
    data: rewardsData,
    isLoading,
    isError,
  } = useReadContract({
    address: stakingAddress,
    abi: [
      {
        inputs: [
          {
            internalType: "address",
            name: "_user",
            type: "address",
          },
          {
            internalType: "uint256[]",
            name: "_licenseIds",
            type: "uint256[]",
          },
        ],
        name: "getUserLicenseRewards",
        outputs: [
          {
            components: [
              {
                internalType: "uint256",
                name: "stakeTime",
                type: "uint256",
              },
              {
                internalType: "uint256",
                name: "totalLockedTime",
                type: "uint256",
              },
              {
                internalType: "uint256",
                name: "totalRewards",
                type: "uint256",
              },
            ],
            internalType: "struct GuardianNodeStakingPool.LicenseLockInfo[]",
            name: "",
            type: "tuple[]",
          },
        ],
        stateMutability: "view",
        type: "function",
      },
    ],
    functionName: "getUserLicenseRewards",
    args: [userAddress, [item.id]],
    query: {
      enabled: !!userAddress && !!stakingAddress,
    },
  });

  if (isLoading) return <Spin size="small" />;
  if (isError)
    return (
      <div className="d-flex-mid">
        <span className="text-blue">0</span>
        <div className="d-flex-left">
          <small className="text-gray-light">$HELA</small>
        </div>
      </div>
    );

  let totalReward = 0;
  if (rewardsData?.[0]) {
    const totalLockedTimeInDays =
      Number(rewardsData[0]?.totalLockedTime) / 86400;

    // Calculate total rewards based on tier
    if (item.node_tier_id === 1) {
      totalReward = Math.min(864, 288 + (totalLockedTimeInDays * 576) / 1460);
    } else {
      totalReward = Math.min(864, (totalLockedTimeInDays * 864) / 1460);
    }
  }

  return (
    <div className="d-flex-mid">
      <span className="text-blue">
        {totalReward
          ? new Intl.NumberFormat("ja-JP", {
              maximumFractionDigits: 5,
            }).format(totalReward)
          : 0}
      </span>
      <div className="d-flex-left">
        <small className="text-gray-light">$HELA</small>
      </div>
    </div>
  );
};

const NftItem = ({
  item,
  stakingAddress,
  userAddress,
  loadingKeyStaking,
  loadingUnStaking,
  handleUnStakeing,
}: {
  item: any;
  stakingAddress: Address;
  userAddress: Address;
  loadingKeyStaking: boolean;
  loadingUnStaking: boolean;
  handleUnStakeing: (id: number) => void;
}) => {
  const {
    data: rewardsData,
    isLoading,
    isError,
  } = useReadContract({
    address: stakingAddress,
    abi: [
      {
        inputs: [
          {
            internalType: "address",
            name: "_user",
            type: "address",
          },
          {
            internalType: "uint256[]",
            name: "_licenseIds",
            type: "uint256[]",
          },
        ],
        name: "getUserLicenseRewards",
        outputs: [
          {
            components: [
              {
                internalType: "uint256",
                name: "stakeTime",
                type: "uint256",
              },
              {
                internalType: "uint256",
                name: "totalLockedTime",
                type: "uint256",
              },
              {
                internalType: "uint256",
                name: "totalRewards",
                type: "uint256",
              },
            ],
            internalType: "struct GuardianNodeStakingPool.LicenseLockInfo[]",
            name: "",
            type: "tuple[]",
          },
        ],
        stateMutability: "view",
        type: "function",
      },
    ],
    functionName: "getUserLicenseRewards",
    args: [userAddress, [item.id]],
    query: {
      enabled: !!userAddress,
    },
  });

  let totalReward = 0;
  let stakeTime = "";
  let totalLockedTime = 0;

  if (!isLoading && !isError && rewardsData) {
    const totalLockedTimeInDays =
      Number(rewardsData[0]?.totalLockedTime) / 86400;

    // Calculate total rewards based on tier
    if (item.node_tier_id === 1) {
      totalReward = Math.min(864, 288 + (totalLockedTimeInDays * 576) / 1460);
    } else {
      totalReward = Math.min(864, (totalLockedTimeInDays * 864) / 1460);
    }

    // Safely assign stakeTime and totalLockedTime
    stakeTime = rewardsData[0]?.stakeTime
      ? String(Number(rewardsData[0]?.stakeTime))
      : "";
    totalLockedTime = rewardsData[0]?.totalLockedTime
      ? Number(rewardsData[0]?.totalLockedTime)
      : 0;
  }

  // Format time units
  let time: any = "";
  let timeUnit: any = "";
  if (totalLockedTime < 3600) {
    time = totalLockedTime / 60;
    timeUnit = "Min";
  }
  if (totalLockedTime > 3600) {
    time = totalLockedTime / 3600;
    timeUnit = "Hour";
  }
  if (totalLockedTime > 86400) {
    time = totalLockedTime / 86400;
    timeUnit = "Day";
    if (time > 1) {
      timeUnit = "Days";
    }
  }

  return (
    <div className="nft-item border-bottom-10">
      <Row gutter={30}>
        <Col xs={12} sm={5}>
          <div className="text-gray-light weight-500">Name</div>
          <div className="text-blue mt-10 bg-blue p-5 w-fitcontent border-5">
            HeLa Guardian Node #{item?.id}
          </div>
        </Col>
        <Col xs={12} sm={5}>
          <div className="text-gray-light weight-500">Tier</div>
          <div className="text-blue mt-10 bg-blue p-5 w-fitcontent border-5">
            {item?.node_tier_id}
          </div>
        </Col>
        <Col xs={12} sm={5} className="mt-0-15-mb">
          <div className="text-gray-light weight-500">Staked Date</div>
          <div className="text-white mt-10 text-uppercase">
            {stakeTime
              ? convertDateTime(new Date(Number(stakeTime) * 1000))
              : "TBA"}
          </div>
        </Col>
        <Col xs={12} sm={5} className="mt-0-15-mb">
          <div className="text-gray-light weight-500">
            Total Staked Duration
          </div>
          <div className="text-white mt-10">
            ~
            {new Intl.NumberFormat("ja-JP", {
              maximumFractionDigits: 1,
            }).format(time)}{" "}
            {timeUnit}
          </div>
        </Col>
        <Col xs={12} sm={5} className="mt-0-15-mb">
          <div className="text-gray-light weight-500">
            <Tooltip placement="bottom" title={"Total Reward"}>
              Total Reward <InfoCircleOutlined />
            </Tooltip>
          </div>
          <div className="text-white mt-10">
            <div className="d-flex-left">
              <span className="text-blue">
                {totalReward
                  ? new Intl.NumberFormat("ja-JP", {
                      maximumFractionDigits: 5,
                    }).format(totalReward)
                  : 0}
              </span>
              <div className="d-flex-left">
                <small className="text-gray-light">$HELA</small>
              </div>
            </div>
          </div>
        </Col>
        <Col xs={12} sm={4} className="mt-0-15-mb">
          {stakingAddress === item?.owner_address && (
            <button
              className="btn-border-blue text-blue"
              disabled={loadingKeyStaking === item?.id && loadingUnStaking}
              onClick={() => handleUnStakeing(item)}
            >
              Unstake{" "}
              {loadingKeyStaking === item?.id && loadingUnStaking && <Spin />}
            </button>
          )}
        </Col>
      </Row>
    </div>
  );
};

const StakedNft = () => {
  const { address } = useAccount();
  const { selectedNetworkId } = useWeb3ModalState();
  const chainId: any = selectedNetworkId;

  const [isModalMint, setIsModalMint] = useState<any>(false);

  const [myNfts, setMyNfts]: any = useState([]);
  const [loading, setLoading]: any = useState(false);
  const [loadingDataMyNft, setLoadingDataMyNft]: any = useState(false);
  const [loadingUnStaking, setLoadingUnStaking]: any = useState(false);
  const [loadingKeyStaking, setLoadingKeyStaking]: any = useState(false);
  const [totalNftRow, setTotalNftRow]: any = useState(0);
  const [stakingAddress, setStakingAddress] = useState<any>("");
  const [nodeUnstake, setNodeUnstake]: any = useState(null);

  let web3: any;
  const connectorId = window.localStorage.getItem("wagmi.recentConnectorId");

  if (connectorId === "io.metamask" || connectorId === "bsc") {
    web3 = new Web3(window.ethereum);
  } else if (connectorId === "com.trustwallet.app") {
    web3 = new Web3(window.trustwallet);
  } else if (connectorId === "com.bitget.web3") {
    web3 = new Web3(window.bitkeep.ethereum);
  } else if (connectorId === "com.okex.wallet") {
    web3 = new Web3(window.okexchain);
  } else {
    web3 = new Web3(window.ethereum);
  }

  let stakeContract: any = new web3.eth.Contract(
    NftNodeStakingAbi as unknown as AbiItem,
    stakingAddress
  );

  const [paramNft, setParamNft]: any = useState({
    page: 1,
    size: 20,
  });

  const getMyNfts = async () => {
    setLoadingDataMyNft(true);
    await BaseApi.getMyStakingNfts(paramNft, address)
      .then(async (res) => {
        if (res.status == 200 && res.data && res.data.data) {
          setTotalNftRow(res.data?.total);

          let arrData: any = [];

          if (res.data.data && res.data.data.length > 0) {
            // Simplified data processing - reward calculations moved to individual NftItem components
            arrData = res.data.data;
          }

          setMyNfts(arrData);
          setLoadingDataMyNft(false);
        }
      })
      .catch((error) => {
        console.log(error);
        setLoadingDataMyNft(false);
      });
  };

  const getSetting = async () => {
    try {
      const response = await BaseApi.getSetting();

      if (response.status == 200 && response.data && response.data?.data) {
        setStakingAddress(response.data?.data?.staking_pool_address);
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    getSetting();
  }, [address]);

  useEffect(() => {
    if (address) {
      getMyNfts();
    }
  }, [address, paramNft, stakingAddress]);

  const onChangeNft_Pagi = (pageNumber: any) => {
    setParamNft({
      ...paramNft,
      page: pageNumber,
    });
  };

  const text = (
    <>
      <p className="mt-0">
        Each Tier 1 HeLa Guardian NFT will receive 288 $HELA airdropped to them
        from the beginning.
      </p>
      <p className="mb-0">
        Staking the NFT allow owners of the NFT to start accruing extra $HELA
        tokens, from their 4-year linear vesting.
      </p>
    </>
  );

  const columns = [
    {
      title: <div className="text-gray-light">Name</div>,
      dataIndex: "name",
      key: "name",
      render: (text: any, record: any) => {
        let nameNode = NodeTier[record?.node_tier_id];
        return (
          <div className="text-blue">HeLa Guardian Node #{record?.id}</div>
        );
      },
    },
    {
      title: <div className="text-gray-light text-center">Tier</div>,
      dataIndex: "node_tier_id",
      key: "node_tier_id",
      render: (text: any, record: any) => {
        return (
          <div className="text-white text-center">{record?.node_tier_id}</div>
        );
      },
    },
    {
      title: <div className="text-gray-light text-center">Staked Date</div>,
      dataIndex: "stakeTime",
      key: "stakeTime",
      render: (text: any, record: any) => {
        return (
          <StakeTimeCell
            item={record}
            stakingAddress={stakingAddress as Address}
            userAddress={address as Address}
          />
        );
      },
    },
    {
      title: (
        <div className="text-gray-light text-center">
          Total Staked Durations
        </div>
      ),
      dataIndex: "totalStakedDate",
      key: "totalStakedDate",
      render: (text: any, record: any) => {
        return (
          <TotalLockedTimeCell
            item={record}
            stakingAddress={stakingAddress as Address}
            userAddress={address as Address}
          />
        );
      },
    },
    {
      title: (
        <div className="text-gray-light text-center">
          <Tooltip placement="bottom" title={text}>
            Total Reward <InfoCircleOutlined />
          </Tooltip>
        </div>
      ),
      dataIndex: "totalReward",
      key: "totalReward",
      render: (text: any, record: any) => {
        return (
          <TotalRewardCell
            item={record}
            stakingAddress={stakingAddress as Address}
            userAddress={address as Address}
          />
        );
      },
    },
    {
      title: <div className="text-gray-light text-center">Status</div>,
      dataIndex: "status",
      key: "status",
      render: (text: any, record: any) => {
        return (
          <div className="text-center">
            {stakingAddress?.toLowerCase() ==
              record?.owner_address?.toLowerCase() && (
              <button
                className="btn-border-blue text-blue"
                disabled={loadingKeyStaking == record?.id && loadingUnStaking}
                onClick={() => handleUnStakeing(record)}
              >
                Unstake{" "}
                {loadingKeyStaking == record?.id && loadingUnStaking && (
                  <Spin />
                )}
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const handleUnStakeing = async (record: any) => {
    setNodeUnstake(record);
    setLoadingUnStaking(true);
    setLoadingKeyStaking(record?.id);
    try {
      if (stakeContract === null || stakingAddress == "") {
        notiError("Stake contract invalid", 4);
        setLoadingUnStaking(false);
        return;
      }

      const gasPrice = await web3.eth.getGasPrice();

      let gasEstimate = await stakeContract.methods
        .unStakeGuardianNode(record?.id)
        .estimateGas({
          from: address,
          gasPrice: web3.utils.toHex(String(gasPrice)),
        });

      await stakeContract.methods
        .unStakeGuardianNode(record?.id)
        .send({
          from: address,
          gas: gasEstimate,
          gasPrice: web3.utils.toHex(String(gasPrice)),
        })
        .then(async (resStake: any) => {
          const interval = setInterval(function () {
            web3.eth.getTransactionReceipt(
              resStake?.transactionHash,
              function (err: any, rec: any) {
                if (rec) {
                  clearInterval(interval);
                  notiSuccess("Unstake Successfully!", 5);

                  setTimeout(() => {
                    setLoadingUnStaking(false);
                    setNodeUnstake(null);
                    window.location.reload();
                  }, 2000);
                }
                if (err) {
                  console.log("err", err);
                  setLoadingUnStaking(false);
                  notiError("Unstake Failed", 3);
                }
              }
            );
          }, 1000);
        })
        .catch((error: any) => {
          console.log("stake", error);
          setIsModalMint(false);
          if (error) {
            if (error.code == 4001 && error.message) {
              notiError(error.message, 5);
            } else if (error.reason) {
              notiError(error.reason, 5);
            } else {
              if (error.data && error.data.message) {
                notiError(error.data.message, 5);
              }
            }
          }
          setLoadingUnStaking(false);
        });
    } catch (error: any) {
      console.log("error", error?.message);
      setIsModalMint(false);
      notiError(error?.message || "Unknown error occurred", 4);
      setLoading(false);
    }
  };

  return (
    <>
      <div className="d-desktop">
        {loadingDataMyNft ? (
          <div className="text-center">
            <Spin size="large" />
          </div>
        ) : (
          <Table
            className="table-delegates"
            bordered={false}
            rowClassName={(record, index) =>
              index % 2 === 0 ? "table-row-light" : "table-row-dark"
            }
            dataSource={myNfts}
            columns={columns}
            pagination={false}
          />
        )}
      </div>
      <div className="d-mobile">
        {address && stakingAddress && myNfts && myNfts.length > 0 ? (
          myNfts.map((item: unknown, key: any) => (
            <NftItem
              handleUnStakeing={handleUnStakeing}
              loadingKeyStaking={loadingKeyStaking}
              loadingUnStaking={loadingUnStaking}
              stakingAddress={stakingAddress}
              userAddress={address}
              key={`aaa${key}`}
              item={item}
            />
          ))
        ) : (
          <div className="text-center">
            <Empty description={<span className="text-white">No data</span>} />
          </div>
        )}
      </div>

      {Number(totalNftRow) > Number(paramNft?.size) && (
        <Pagination
          current={paramNft.page}
          defaultCurrent={1}
          total={totalNftRow}
          pageSize={paramNft.size}
          onChange={onChangeNft_Pagi}
          showSizeChanger={false}
          style={{ marginTop: "15px" }}
          className="pagiation-custom mt-15"
        />
      )}

      <Modal
        footer={false}
        className="modal-mint"
        title={<></>}
        open={loadingUnStaking}
      >
        <div className="text-center text-white font-20 mt-0 border-bottom-10 mb-15">
          Unstake {NodeTier[nodeUnstake?.node_tier_id]}
        </div>

        <div className="text-center mb-15">
          <Spin size="large" />
        </div>
      </Modal>
    </>
  );
};
export default StakedNft;
